import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  ArrowLeft,
  X,
  Award,
  Users,
  Tag,
  Briefcase,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type {
  IReservationCategory,
  ReservationForm,
  IReservationApplicableTo,
  ReservationType
} from "../types/quarters.types";
import {
  RESERVATION_CATEGORY_CODES,
  COMMON_DESIGNATIONS,
} from "../types/quarters.types";

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM: ReservationForm = {
  name: "",
  code: "",
  type: "percentage",
  value: 0,
  description: "",
  applicableTo: { gender: "", categories: [], designations: [] },
};

/**
 * Pre-seeded default categories that can be created on first setup.
 * Admin clicks "Seed Defaults" and these are created.
 */
export const DEFAULT_CATEGORIES: (Omit<ReservationForm, "applicableTo"> & {
  applicableTo: IReservationApplicableTo;
})[] = [
    {
      name: "Women Quota",
      code: "WOMEN",
      type: "percentage",
      value: 15,
      description: "15% of flats reserved for women employees",
      applicableTo: { gender: "Female", categories: [], designations: [] },
    },
    {
      name: "PWD Quota",
      code: "PWD",
      type: "percentage",
      value: 5,
      description: "5% of flats reserved for Persons with Disabilities",
      applicableTo: { gender: "", categories: ["PWD"], designations: [] },
    },
    {
      name: "SC/ST Quota",
      code: "SCST",
      type: "percentage",
      value: 22,
      description: "22% for SC (15%) + ST (7%) reserved category employees",
      applicableTo: { gender: "", categories: ["SC", "ST"], designations: [] },
    },
    {
      name: "OBC Quota",
      code: "OBC",
      type: "percentage",
      value: 27,
      description: "27% of flats reserved for OBC employees",
      applicableTo: { gender: "", categories: ["OBC"], designations: [] },
    },
    {
      name: "Director Residence",
      code: "DIR",
      type: "flat",
      value: 1,
      description: "1 flat exclusively reserved for the Director",
      applicableTo: { gender: "", categories: [], designations: ["Director"] },
    },
    {
      name: "AO / Admin Officer",
      code: "AO",
      type: "flat",
      value: 2,
      description: "Reserved flats for Administrative Officers (AO / DDA)",
      applicableTo: { gender: "", categories: [], designations: ["AO", "DDA"] },
    },
  ];
// ─── Component ────────────────────────────────────────────────────────────────

const ReservationSettings = () => {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [categories, setCategories] = useState<IReservationCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<IReservationCategory | null>(null);
  const [form, setForm] = useState<ReservationForm>(EMPTY_FORM);

  // Custom designation input
  const [customDesig, setCustomDesig] = useState("");

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await quartersService.getReservations();
      if (res.success) setCategories(res.data);
    } catch (err: any) {
      toast.error(err.message || "Failed to load reservation categories");
    } finally {
      setLoading(false);
    }
  }, [quartersService, toast]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const updateApplicableTo = (patch: Partial<IReservationApplicableTo>) => {
    setForm((f) => ({ ...f, applicableTo: { ...f.applicableTo, ...patch } }));
  };

  const toggleCategory = (code: string) => {
    const existing = form.applicableTo?.categories || [];
    updateApplicableTo({
      categories: existing.includes(code)
        ? existing.filter((c) => c !== code)
        : [...existing, code],
    });
  };

  const toggleDesignation = (d: string) => {
    const existing = form.applicableTo?.designations || [];
    updateApplicableTo({
      designations: existing.includes(d)
        ? existing.filter((x) => x !== d)
        : [...existing, d],
    });
  };

  const addCustomDesig = () => {
    const trimmed = customDesig.trim();
    if (!trimmed) return;
    const existing = form.applicableTo?.designations || [];
    if (!existing.includes(trimmed)) {
      updateApplicableTo({ designations: [...existing, trimmed] });
    }
    setCustomDesig("");
  };

  const removeDesig = (d: string) => {
    const existing = form.applicableTo?.designations || [];
    updateApplicableTo({ designations: existing.filter((x) => x !== d) });
  };

  // ── CRUD ───────────────────────────────────────────────────────────────────

  const handleOpenModal = (category: IReservationCategory | null = null) => {
    if (category) {
      setEditingCategory(category);
      setForm({
        name: category.name,
        code: category.code,
        type: category.type,
        value: category.value,
        description: category.description || "",
        applicableTo: {
          gender: category.applicableTo?.gender || "",
          categories: category.applicableTo?.categories || [],
          designations: category.applicableTo?.designations || [],
        },
      });
    } else {
      setEditingCategory(null);
      setForm(EMPTY_FORM);
    }
    setCustomDesig("");
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        const res = await quartersService.updateReservation(editingCategory._id, form);
        if (res.success) {
          toast.success("Reservation category updated");
          loadCategories();
          setIsModalOpen(false);
        }
      } else {
        const res = await quartersService.createReservation(form);
        if (res.success) {
          toast.success("Reservation category created");
          loadCategories();
          setIsModalOpen(false);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save reservation category");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      const res = await quartersService.deleteReservation(id);
      if (res.success) {
        toast.success("Category removed successfully");
        loadCategories();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to remove category");
    }
  };

  const handleSeedDefaults = async () => {
    if (
      !window.confirm(
        `This will create ${DEFAULT_CATEGORIES.length} standard reservation categories (Women, PWD, SC/ST, OBC, Director, AO). Continue?`
      )
    )
      return;
    setSeeding(true);
    let created = 0;
    for (const cat of DEFAULT_CATEGORIES) {
      try {
        const res = await quartersService.createReservation(cat);
        if (res.success) created++;
      } catch {
        // skip duplicates silently
      }
    }
    toast.success(`Seeded ${created} default reservation categories`);
    loadCategories();
    setSeeding(false);
  };

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <Award className="w-8 h-8 text-blue-600" /> Reservation & Quota Policy
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Define flat reservation quotas for specific employee groups — by gender, category (PWD/SC/OBC), or designation (DDA/AO/MS).
          </p>
        </div>
        <div className="flex gap-3">
          {categories.length === 0 && (
            <button
              onClick={handleSeedDefaults}
              disabled={seeding}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-4 py-3 rounded-xl border border-slate-200 transition flex items-center gap-2 text-sm disabled:opacity-60"
            >
              {seeding ? "Seeding..." : "Seed Defaults"}
            </button>
          )}
          <button
            onClick={() => handleOpenModal()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-xl transition shadow-md shadow-blue-200 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" /> Add Category
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-2xl mb-8 flex items-start gap-3">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
        <div className="text-xs space-y-1">
          <p className="font-bold">How reservation policies work:</p>
          <p>
            Each category defines how many flats are set aside (as a % or fixed count) for a target group.
            Assign a flat to a category using the <strong>Reserved For</strong> field in Flat Management.
            The allotment engine respects these during automatic allocation.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-3xl">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-slate-500 text-sm font-semibold mt-3">Loading reservation categories...</p>
        </div>
      ) : categories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat) => (
            <div
              key={cat._id}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col justify-between hover:shadow-md hover:border-slate-300 transition"
            >
              <div className="space-y-3">
                {/* Title row */}
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <span className="bg-slate-100 text-slate-700 text-[10px] px-2 py-0.5 rounded font-bold uppercase">
                      {cat.code}
                    </span>
                    <h3 className="text-base font-bold text-slate-800 mt-1 font-serif">{cat.name}</h3>
                  </div>
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase flex-shrink-0 ${cat.type === "percentage"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-indigo-100 text-indigo-800"
                      }`}
                  >
                    {cat.type}
                  </span>
                </div>

                {/* Quota value */}
                <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                  <p className="text-slate-400 text-[10px] font-bold uppercase">Allotment Rule</p>
                  <p className="text-xl font-black text-slate-800 mt-0.5">
                    {cat.type === "percentage"
                      ? `${cat.value}% of Flats`
                      : `${cat.value} Fixed Flat${cat.value !== 1 ? "s" : ""}`}
                  </p>
                </div>

                {/* Applicable to */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
                    <Users className="w-3 h-3" /> Applicable To
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.applicableTo?.gender && (
                      <span className="bg-pink-50 border border-pink-100 text-pink-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {cat.applicableTo.gender}
                      </span>
                    )}
                    {cat.applicableTo?.categories?.map((c) => (
                      <span key={c} className="bg-amber-50 border border-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {c}
                      </span>
                    ))}
                    {cat.applicableTo?.designations?.map((d) => (
                      <span key={d} className="bg-emerald-50 border border-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {d}
                      </span>
                    ))}
                    {!cat.applicableTo?.gender &&
                      !cat.applicableTo?.categories?.length &&
                      !cat.applicableTo?.designations?.length && (
                        <span className="text-slate-400 text-[10px] italic">Any eligible employee</span>
                      )}
                  </div>
                </div>

                {cat.description && (
                  <p className="text-slate-500 text-xs leading-relaxed">{cat.description}</p>
                )}
              </div>

              <div className="flex justify-end border-t border-slate-100 pt-4 mt-4 gap-2">
                <button
                  onClick={() => handleOpenModal(cat)}
                  className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                  title="Edit Category"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(cat._id)}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                  title="Delete Category"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
          <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-700">No Reservation Categories</h2>
          <p className="text-slate-500 text-sm mt-1 mb-6">
            Define who gets reserved flats — women, PWD, specific designations like DDA/AO/MS, or caste categories.
          </p>
          <button
            onClick={handleSeedDefaults}
            disabled={seeding}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold px-6 py-3 rounded-xl transition shadow-md shadow-blue-200 text-sm"
          >
            {seeding ? "Seeding defaults..." : "Seed Standard Categories (Women, PWD, SC/ST, OBC, AO, Director)"}
          </button>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-xl w-full max-w-xl overflow-hidden my-4">
            <div className="flex justify-between items-center bg-slate-50 border-b border-slate-100 px-6 py-4">
              <h3 className="text-slate-800 font-bold text-base font-serif">
                {editingCategory ? "Edit Reservation Category" : "Create Reservation Category"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
              {/* Name + Code */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Category Name*</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="e.g. Women Quota"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Code*</label>
                  <input
                    type="text"
                    required
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition uppercase"
                    placeholder="e.g. WOMEN"
                  />
                </div>
              </div>

              {/* Type + Value */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Quota Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value?.toString() as ReservationType })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="percentage">Percentage of Flats</option>
                    <option value="flat">Fixed Flat Count</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">
                    {form.type === "percentage" ? "Percentage Value (%)*" : "Fixed Count*"}
                  </label>
                  <input
                    type="number"
                    required
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    min={0}
                    max={form.type === "percentage" ? 100 : undefined}
                  />
                </div>
              </div>

              {/* ─── Applicable To ─────────────────────────────────────────── */}
              <div className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-100 px-4 py-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <p className="text-xs font-bold text-slate-700 uppercase">Applicable To (Target Group)</p>
                </div>
                <div className="p-4 space-y-4">
                  {/* Gender */}
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Gender Restriction
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {["", "Female", "Male", "Other"].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => updateApplicableTo({ gender: g })}
                          className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition ${(form.applicableTo?.gender || "") === g
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                            }`}
                        >
                          {g === "" ? "Any Gender" : g}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Employee Category */}
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Employee Category (Select multiple)
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {RESERVATION_CATEGORY_CODES.map((code) => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => toggleCategory(code)}
                          className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition ${form.applicableTo?.categories?.includes(code)
                            ? "bg-amber-500 text-white border-amber-500"
                            : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"
                            }`}
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Designations */}
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1">
                      <Briefcase className="w-3 h-3" /> Designations (Select or add custom)
                    </label>
                    <div className="flex gap-1.5 flex-wrap">
                      {COMMON_DESIGNATIONS.map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDesignation(d)}
                          className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border transition ${form.applicableTo?.designations?.includes(d)
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                            }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>

                    {/* Custom designation input */}
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={customDesig}
                        onChange={(e) => setCustomDesig(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomDesig())}
                        placeholder="Add custom designation..."
                        className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      />
                      <button
                        type="button"
                        onClick={addCustomDesig}
                        className="bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold px-3 py-2 rounded-lg transition"
                      >
                        Add
                      </button>
                    </div>

                    {/* Selected designations pill row */}
                    {(form.applicableTo?.designations?.length ?? 0) > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {form.applicableTo?.designations?.map((d) => (
                          <span
                            key={d}
                            className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
                          >
                            {d}
                            <button
                              type="button"
                              onClick={() => removeDesig(d)}
                              className="text-emerald-600 hover:text-red-600 ml-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-slate-500 uppercase">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  rows={2}
                  placeholder="Details of reservation category..."
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition shadow-md shadow-blue-200"
                >
                  {editingCategory ? "Update Category" : "Create Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationSettings;
